import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { useContacts } from '../../contexts/ContactContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiCheck, FiDownload, FiUpload, FiX } from 'react-icons/fi';


const ImportModal = ({ onClose }) => {
  const { bulkImportContacts } = useContacts();
  const [step, setStep] = useState(1);
  const [csvData, setCsvData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      Papa.parse(file, {
        complete: (results) => {
          setCsvData(results.data);
          setStep(2);
        },
        header: true,
        skipEmptyLines: true
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    multiple: false
  });

  const csvFields = csvData.length > 0 ? Object.keys(csvData[0]) : [];
  const crmFields = [
    { key: 'firstName', label: 'First Name', required: true },
    { key: 'lastName', label: 'Last Name', required: false },
    { key: 'email', label: 'Email', required: true },
    { key: 'phone', label: 'Phone', required: false },
    { key: 'company', label: 'Company', required: false },
    { key: 'title', label: 'Title', required: false }
  ];

  const handleMapping = (crmField, csvField) => {
    setMapping(prev => ({
      ...prev,
      [crmField]: csvField
    }));
  };

  const handleImport = async () => {
    setImporting(true);

    try {
      if (!mapping.firstName || !mapping.email) {
        toast.error('Please map the required First Name and Email fields.');
        return;
      }

      const mappedContacts = csvData
        .map(row => {
          const contact = {};

          Object.entries(mapping).forEach(([crmField, csvField]) => {
            if (csvField && row[csvField]) {
              contact[crmField] = row[csvField];
            }
          });

          return contact;
        })
        .filter(contact => contact.firstName && contact.email);

      if (mappedContacts.length === 0) {
        toast.error('No valid contacts were found with the selected mappings.');
        return;
      }

      const importedCount = await bulkImportContacts(mappedContacts);

      if (importedCount > 0) {
        onClose();
      }
    } catch (error) {
      console.error('Error importing contacts:', error);
      toast.error('Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'firstName,lastName,email,phone,company,title\nJohn,Doe,john@example.com,+1234567890,Acme Corp,Manager\nJane,Smith,jane@example.com,+0987654321,Tech Inc,Developer';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Import Contacts</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload CSV File</h3>
                <p className="text-gray-600 mb-6">
                  Upload a CSV file containing your contacts. Make sure it includes at least first name and email columns.
                </p>
              </div>

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragActive
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                <SafeIcon icon={FiUpload} className="text-4xl text-gray-400 mx-auto mb-4" />
                {isDragActive ? (
                  <p className="text-blue-600">Drop the file here...</p>
                ) : (
                  <div>
                    <p className="text-gray-600 mb-2">
                      Drag & drop a CSV file here, or click to select
                    </p>
                    <p className="text-sm text-gray-500">
                      Supports CSV, XLS, and XLSX files
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <SafeIcon icon={FiDownload} className="text-sm" />
                  Download CSV Template
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Map Fields</h3>
                <p className="text-gray-600 mb-6">
                  Match your CSV columns to the CRM fields. Required fields are marked with *.
                </p>
              </div>

              <div className="space-y-4">
                {crmFields.map((field) => (
                  <div key={field.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <label className="font-medium text-gray-900">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                    </div>
                    <select
                      value={mapping[field.key] || ''}
                      onChange={(e) => handleMapping(field.key, e.target.value)}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select column</option>
                      {csvFields.map((csvField) => (
                        <option key={csvField} value={csvField}>
                          {csvField}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Preview</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Found {csvData.length} records in your file
                </p>
                {csvData.slice(0, 3).map((row, index) => (
                  <div key={index} className="text-sm text-gray-700 mb-1">
                    {mapping.firstName && row[mapping.firstName]} {mapping.lastName && row[mapping.lastName]} - {mapping.email && row[mapping.email]}
                  </div>
                ))}
                {csvData.length > 3 && (
                  <p className="text-sm text-gray-500">...and {csvData.length - 3} more</p>
                )}
              </div>

              <div className="flex justify-between pt-6 border-t border-gray-200">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={!mapping.firstName || !mapping.email || importing}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importing ? (
                    <div className="spinner" />
                  ) : (
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  )}
                  {importing ? 'Importing...' : 'Import Contacts'}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ImportModal;